import React from 'react';

import { socialCardKeys } from '@/src/constants/socialcard.constant';
import { MediaBox, MediaOverlay, WaitingSpinner, MediaEnclosure, MediaSummary, playerRadius } from './StudioMediaParts';

/**
 * Audio and picture testimonials for the studio card set.
 *
 * Video is NOT here - see StudioVideo.jsx and the note in StudioMediaParts.
 * Audio stays static: it needs no hls.js, only a plain <audio> element and
 * the Web Audio analyser for the waveform.
 */

export { MediaSummary } from './StudioMediaParts';

/* --------------------------------------------------------------- picture */

export const StudioPicture = ({ t, cardItem, radius = 10, style }) => {
  const src =
    (cardItem && cardItem[socialCardKeys.messageMediaImage]) ||
    (cardItem && cardItem[socialCardKeys.screenshotImage]);

  if (!src) {
    return (
      <div
        className={t.fontClass}
        style={{
          width: '100%',
          borderRadius: radius,
          background: t.soft,
          color: t.muted,
          fontSize: 12.5,
          padding: '22px 12px',
          textAlign: 'center',
          ...(style || {}),
        }}
      >
        Picture testimonial
      </div>
    );
  }

  return (
    <div className="w-full overflow-hidden" style={{ borderRadius: radius, ...(style || {}) }}>
      <img
        src={src}
        alt="Testimonial"
        onLoad={() => t.refresh && t.refresh()}
        style={{ width: '100%', display: 'block', borderRadius: radius, objectFit: 'cover' }}
      />
    </div>
  );
};

/* ----------------------------------------------------------------- audio */

/** The idle wave, and the live waveform - lifted from the older audio card so
 *  the audio testimonial looks the same here as on every other widget type. */
const drawIdleWave = (canvas) => {
  const ctx = canvas.getContext('2d');
  if (!ctx) return;
  const WIDTH = canvas.width;
  const HEIGHT = canvas.height;

  ctx.fillStyle = 'rgb(13, 17, 23)';
  ctx.fillRect(0, 0, WIDTH, HEIGHT);

  const gradient = ctx.createLinearGradient(0, 0, WIDTH, 0);
  gradient.addColorStop(0, 'rgba(0, 162, 255, 0.8)');
  gradient.addColorStop(1, 'rgba(0, 255, 255, 0.8)');

  ctx.strokeStyle = gradient;
  ctx.lineWidth = 2;

  const amplitude = HEIGHT * 0.05;
  const wavelength = WIDTH / 3;

  ctx.beginPath();
  ctx.moveTo(0, HEIGHT / 2);
  for (let x = 0; x <= WIDTH; x += 5) {
    ctx.lineTo(x, HEIGHT / 2 + amplitude * Math.sin((x / wavelength) * Math.PI * 2));
  }
  ctx.stroke();

  ctx.beginPath();
  ctx.moveTo(0, HEIGHT / 2);
  for (let x = 0; x <= WIDTH; x += 5) {
    ctx.lineTo(x, HEIGHT / 2 - amplitude * Math.sin((x / wavelength) * Math.PI * 2));
  }
  ctx.shadowBlur = 15;
  ctx.shadowColor = 'rgba(0, 200, 255, 0.5)';
  ctx.stroke();
  ctx.shadowBlur = 0;
};

const drawWaveform = (canvas, analyser) => {
  const ctx = canvas.getContext('2d');
  if (!ctx) return;
  const WIDTH = canvas.width;
  const HEIGHT = canvas.height;

  const timeData = new Uint8Array(analyser.fftSize);
  analyser.getByteTimeDomainData(timeData);

  const freqData = new Uint8Array(analyser.frequencyBinCount);
  analyser.getByteFrequencyData(freqData);
  const intensity = freqData.reduce((a, b) => a + b, 0) / freqData.length / 255;

  ctx.fillStyle = 'rgb(13, 17, 23)';
  ctx.fillRect(0, 0, WIDTH, HEIGHT);

  const gradient = ctx.createLinearGradient(0, 0, WIDTH, 0);
  gradient.addColorStop(0, `rgba(0, ${162 + intensity * 93}, 255, 1)`);
  gradient.addColorStop(1, `rgba(0, 255, ${255 - intensity * 100}, 1)`);

  ctx.strokeStyle = gradient;
  ctx.lineWidth = 3;
  ctx.lineJoin = 'round';
  ctx.beginPath();

  const sliceWidth = WIDTH / (timeData.length / 2);
  let x = 0;
  ctx.moveTo(0, HEIGHT / 2);

  for (let i = 0; i < timeData.length; i += 2) {
    const y = (timeData[i] / 128.0) * (HEIGHT / 2);
    const jitter = Math.random() * 2 - 1;
    if (i === 0) {
      ctx.moveTo(x, y + jitter);
    } else {
      const prevX = x - sliceWidth;
      const prevY = (timeData[i - 2] / 128.0) * (HEIGHT / 2);
      ctx.quadraticCurveTo(prevX + (x - prevX) / 2, prevY, x, y + jitter);
    }
    x += sliceWidth;
  }

  for (let i = timeData.length - 1; i >= 0; i -= 2) {
    const y = HEIGHT - (timeData[i] / 128.0) * (HEIGHT / 2);
    const jitter = Math.random() * 2 - 1;
    x -= sliceWidth;
    ctx.lineTo(x, y + jitter);
  }

  ctx.lineTo(0, HEIGHT / 2);
  ctx.closePath();
  ctx.fillStyle = gradient;
  ctx.globalAlpha = 0.8;
  ctx.fill();
  ctx.globalAlpha = 1;
  ctx.stroke();
};

export const StudioAudio = ({ t, cardItem, enclosure, summaryStyle }) => {
  const audioRef = React.useRef(null);
  const canvasRef = React.useRef(null);
  const animationRef = React.useRef(null);
  const sourceRef = React.useRef(null);

  const [isPlaying, setIsPlaying] = React.useState(false);
  const [isWaiting, setIsWaiting] = React.useState(false);

  const src = cardItem && cardItem[socialCardKeys.audioAssetUrl];

  React.useEffect(() => {
    const canvas = canvasRef.current;
    const audio = audioRef.current;
    if (!canvas) return undefined;

    drawIdleWave(canvas);
    if (!audio || !src) return undefined;

    let audioContext;
    let analyser;

    /* The visualizer is decoration: if Web Audio is unavailable or the asset
       isn't CORS-readable, the player must still play. Everything below is
       therefore best-effort and never rethrows. */
    try {
      audioContext = new (window.AudioContext || window.webkitAudioContext)();
      analyser = audioContext.createAnalyser();
      analyser.fftSize = 2048;
      sourceRef.current = audioContext.createMediaElementSource(audio);
      sourceRef.current.connect(analyser);
      analyser.connect(audioContext.destination);
    } catch (e) {
      audioContext = null;
      analyser = null;
    }

    const loop = () => {
      animationRef.current = requestAnimationFrame(loop);
      if (analyser) drawWaveform(canvas, analyser);
    };

    const onPlay = () => {
      if (audioContext && audioContext.state === 'suspended') audioContext.resume();
      if (animationRef.current) cancelAnimationFrame(animationRef.current);
      if (analyser) loop();
    };
    const onStop = () => {
      if (animationRef.current) cancelAnimationFrame(animationRef.current);
      animationRef.current = null;
      drawIdleWave(canvas);
    };

    audio.addEventListener('play', onPlay);
    audio.addEventListener('pause', onStop);
    audio.addEventListener('ended', onStop);

    return () => {
      audio.removeEventListener('play', onPlay);
      audio.removeEventListener('pause', onStop);
      audio.removeEventListener('ended', onStop);
      if (animationRef.current) cancelAnimationFrame(animationRef.current);
      if (audioContext && audioContext.state !== 'closed') {
        try {
          audioContext.close();
        } catch (e) {
          // already closed
        }
      }
    };
  }, [src]);

  const togglePlay = () => {
    const audio = audioRef.current;
    if (!audio || !src) return;
    if (audio.paused) audio.play();
    else audio.pause();
  };

  return (
    <MediaEnclosure enclosure={enclosure} cardItem={cardItem}>
      {/* 16:9 - what the older audio cards ACTUALLY render. Their markup asks
          for aspect-[21/9], but that is an arbitrary Tailwind value and all
          three repos are on Tailwind v2 with no JIT and no aspectRatio plugin,
          so the class compiles to nothing; the height falls back to the
          canvas's intrinsic 1920x1080. Match the real output, not the dead
          class - 21:9 here made the player visibly flatter than every other
          widget type. */}
      <MediaBox ratio={16 / 9} radius={playerRadius(enclosure, cardItem)}>
        <canvas ref={canvasRef} width={1920} height={1080} style={{ width: '100%', height: '100%' }} />

        {src ? (
          <audio
            crossOrigin="anonymous"
            ref={audioRef}
            src={src}
            preload="metadata"
            onPlay={() => {
              setIsPlaying(true);
              setIsWaiting(false);
            }}
            onPlaying={() => {
              setIsPlaying(true);
              setIsWaiting(false);
            }}
            onPause={() => {
              setIsPlaying(false);
              setIsWaiting(false);
            }}
            onWaiting={() => setIsWaiting(true)}
            onEnded={() => setIsPlaying(false)}
            style={{ display: 'none' }}
          />
        ) : null}

        {/* Always mounted, unlike video: the waveform canvas takes no clicks,
            so this bar is the only way to pause. */}
        <MediaOverlay
          cardItem={cardItem}
          onPlay={togglePlay}
          fontClass={t.fontClass}
          isPlaying={isPlaying}
        />
        {isWaiting ? <WaitingSpinner /> : null}
      </MediaBox>
      <MediaSummary t={t} cardItem={cardItem} color={enclosure && enclosure.color} summaryStyle={summaryStyle} />
    </MediaEnclosure>
  );
};
